import * as THREE from "three";

import type { ShipFrameId } from "../content/schemas";
import { createMetalPbrMaps } from "./textures";

/**
 * Visual-only scale. Peer to enemy kits after ENEMY_VISUAL_SCALE (~2–5 m),
 * not the old 4.8× giant-bird silhouette. Sim collision stays on PLAYER_RADIUS_M.
 */
const SHIP_SCALE = 1.28;

/** Shared gritty industrial kit materials — muted steel, scarred plates, restrained emissives. */
function makeMaterials(): {
  hull: THREE.MeshStandardMaterial;
  plate: THREE.MeshStandardMaterial;
  dark: THREE.MeshStandardMaterial;
  glow: THREE.MeshStandardMaterial;
  engine: THREE.MeshStandardMaterial;
} {
  const hullPbr = createMetalPbrMaps(0x8a9096);
  hullPbr.map.repeat.set(1.5, 1.5);
  hullPbr.roughnessMap.repeat.set(1.5, 1.5);
  hullPbr.metalnessMap.repeat.set(1.5, 1.5);
  const platePbr = createMetalPbrMaps(0x5a544c);
  platePbr.map.repeat.set(1.2, 1.2);
  platePbr.roughnessMap.repeat.set(1.2, 1.2);
  platePbr.metalnessMap.repeat.set(1.2, 1.2);
  return {
    hull: new THREE.MeshStandardMaterial({
      color: 0x8a9096,
      map: hullPbr.map,
      roughnessMap: hullPbr.roughnessMap,
      metalnessMap: hullPbr.metalnessMap,
      metalness: 0.68,
      roughness: 0.45,
      envMapIntensity: 0.42,
    }),
    plate: new THREE.MeshStandardMaterial({
      color: 0x5a544c,
      map: platePbr.map,
      roughnessMap: platePbr.roughnessMap,
      metalnessMap: platePbr.metalnessMap,
      metalness: 0.5,
      roughness: 0.68,
      envMapIntensity: 0.36,
    }),
    dark: new THREE.MeshStandardMaterial({
      color: 0x2e2a26,
      map: platePbr.map,
      roughnessMap: platePbr.roughnessMap,
      metalnessMap: platePbr.metalnessMap,
      metalness: 0.62,
      roughness: 0.58,
      envMapIntensity: 0.3,
    }),
    glow: new THREE.MeshStandardMaterial({
      color: 0x9a7a32,
      emissive: 0x4a3008,
      emissiveIntensity: 1.2,
      metalness: 0.35,
      roughness: 0.42,
      toneMapped: false,
    }),
    engine: new THREE.MeshStandardMaterial({
      color: 0x3a454c,
      emissive: 0xa04a20,
      emissiveIntensity: 1.35,
      metalness: 0.4,
      roughness: 0.38,
      envMapIntensity: 0.28,
      toneMapped: false,
    }),
  };
}

function box(
  w: number,
  h: number,
  d: number,
  mat: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  rx = 0,
  ry = 0,
  rz = 0,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  return mesh;
}

function cyl(
  rTop: number,
  rBot: number,
  h: number,
  mat: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  rx = 0,
  ry = 0,
  rz = 0,
  segments = 6,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, segments), mat);
  mesh.position.set(x, y, z);
  mesh.rotation.set(rx, ry, rz);
  return mesh;
}

/**
 * Builds a low-poly player ship. Local +X is the nose (matches sim facing 0 = +X).
 * Top-down chase cam reads silhouette from above — keep planforms hard-edged and
 * industrial (fuselage / pods / thrusters), never diamond-bird or penguin.
 */
export function createShipMesh(frameId: string): THREE.Group {
  const id = (frameId in FRAME_BUILDERS ? frameId : "skimmer") as ShipFrameId;
  const mats = makeMaterials();
  const root = new THREE.Group();
  root.name = `ship_${id}`;
  FRAME_BUILDERS[id](root, mats);
  root.scale.setScalar(SHIP_SCALE);
  return root;
}

type Mats = ReturnType<typeof makeMaterials>;
type Builder = (root: THREE.Group, mats: Mats) => void;

/**
 * Skimmer — compact frontier fighter.
 * Planform: long box fuselage, side engine pods, hard strakes, blunt sensor prow.
 */
const buildSkimmer: Builder = (root, m) => {
  root.add(
    // Main fuselage spine (reads as a hull, not a diamond body).
    box(2.35, 0.42, 0.72, m.hull, 0.05, 0.12, 0),
    // Belly plate / ventral armor.
    box(1.9, 0.14, 0.95, m.dark, -0.1, 0.0, 0),
    // Blunt industrial prow + sensor spike.
    box(0.55, 0.34, 0.55, m.plate, 1.3, 0.14, 0),
    cyl(0.07, 0.09, 0.55, m.glow, 1.72, 0.16, 0, 0, 0, Math.PI / 2, 5),
    // Cockpit canopy blister (small cab, mid-forward — not a “head”).
    box(0.55, 0.28, 0.42, m.glow, 0.45, 0.4, 0),
    box(0.35, 0.12, 0.5, m.dark, 0.45, 0.52, 0),
    // Hard rectangular wing strakes (plate, not flippers).
    box(1.15, 0.08, 0.28, m.plate, 0.05, 0.06, 0.72),
    box(1.15, 0.08, 0.28, m.plate, 0.05, 0.06, -0.72),
    // Twin side engine pods with glowing nozzle faces aft.
    box(1.05, 0.32, 0.38, m.engine, -0.55, 0.1, 0.78),
    box(1.05, 0.32, 0.38, m.engine, -0.55, 0.1, -0.78),
    cyl(0.14, 0.18, 0.22, m.glow, -1.15, 0.1, 0.78, 0, 0, Math.PI / 2, 6),
    cyl(0.14, 0.18, 0.22, m.glow, -1.15, 0.1, -0.78, 0, 0, Math.PI / 2, 6),
    // Dorsal antenna ridge.
    box(0.7, 0.16, 0.12, m.dark, -0.35, 0.38, 0),
  );
};

/**
 * Hauler — wide salvage brick.
 * Planform: cargo bay slab, blunt cab nose, fat twin thrusters, stub side rails.
 */
const buildHauler: Builder = (root, m) => {
  root.add(
    // Wide cargo brick.
    box(2.1, 0.55, 1.55, m.hull, -0.15, 0.18, 0),
    // Raised cargo lid / bay roof.
    box(1.45, 0.42, 1.25, m.plate, -0.45, 0.52, 0),
    // Blunt cab / prow block.
    box(0.7, 0.45, 1.05, m.dark, 1.15, 0.22, 0),
    box(0.4, 0.28, 0.7, m.glow, 1.35, 0.42, 0),
    // Side cargo rails (hard boxes, not wings).
    box(1.4, 0.18, 0.28, m.plate, 0.0, 0.08, 1.0),
    box(1.4, 0.18, 0.28, m.plate, 0.0, 0.08, -1.0),
    // Fat twin aft thrusters.
    box(0.75, 0.42, 0.55, m.engine, -1.35, 0.14, 0.55),
    box(0.75, 0.42, 0.55, m.engine, -1.35, 0.14, -0.55),
    cyl(0.18, 0.24, 0.2, m.glow, -1.8, 0.14, 0.55, 0, 0, Math.PI / 2, 6),
    cyl(0.18, 0.24, 0.2, m.glow, -1.8, 0.14, -0.55, 0, 0, Math.PI / 2, 6),
    // Ventral skid plates.
    box(1.2, 0.1, 0.35, m.dark, 0.2, -0.05, 0.55),
    box(1.2, 0.1, 0.35, m.dark, 0.2, -0.05, -0.55),
    // Stack beacon.
    box(0.25, 0.35, 0.25, m.glow, -0.6, 0.8, 0),
  );
};

/**
 * Interceptor — long needle fighter.
 * Planform: thin fuselage boom, swept hard wing plates, twin aft engine cylinders.
 */
const buildInterceptor: Builder = (root, m) => {
  root.add(
    // Long thin fuselage.
    box(2.7, 0.28, 0.42, m.hull, 0.1, 0.1, 0),
    // Nose spike / sensor lance.
    cyl(0.06, 0.1, 0.7, m.glow, 1.7, 0.12, 0, 0, 0, Math.PI / 2, 5),
    box(0.45, 0.22, 0.32, m.plate, 1.2, 0.12, 0),
    // Compact cockpit fairing.
    box(0.45, 0.2, 0.28, m.glow, 0.55, 0.3, 0),
    // Swept hard wing plates (angled boxes — machine wings, not feathers).
    box(1.55, 0.07, 0.22, m.dark, -0.05, 0.05, 0.85, 0, 0.42, 0),
    box(1.55, 0.07, 0.22, m.dark, -0.05, 0.05, -0.85, 0, -0.42, 0),
    // Twin boom engines flanking aft.
    box(1.15, 0.22, 0.28, m.engine, -0.95, 0.08, 0.55),
    box(1.15, 0.22, 0.28, m.engine, -0.95, 0.08, -0.55),
    cyl(0.1, 0.14, 0.28, m.glow, -1.6, 0.08, 0.55, 0, 0, Math.PI / 2, 6),
    cyl(0.1, 0.14, 0.28, m.glow, -1.6, 0.08, -0.55, 0, 0, Math.PI / 2, 6),
    // Dorsal fin / heat sink (thin plate — not a crest).
    box(0.55, 0.28, 0.08, m.plate, -0.2, 0.32, 0),
    // Ventral keel strip.
    box(1.6, 0.08, 0.18, m.dark, 0.0, 0.0, 0),
  );
};

/**
 * Bulwark — armored prow tank.
 * Planform: wide slab hull, forward shield plow, heavy side armor, stub thrusters.
 */
const buildBulwark: Builder = (root, m) => {
  root.add(
    // Wide armored hull slab.
    box(1.85, 0.55, 1.55, m.hull, -0.2, 0.18, 0),
    // Forward shield plow / armored face (thick vertical plate).
    box(0.35, 0.75, 1.85, m.dark, 1.05, 0.28, 0),
    box(0.22, 0.55, 1.55, m.plate, 1.28, 0.22, 0),
    // Reinforced shoulder armor blocks.
    box(0.85, 0.4, 0.4, m.plate, 0.25, 0.22, 0.95),
    box(0.85, 0.4, 0.4, m.plate, 0.25, 0.22, -0.95),
    // Cockpit block set into hull (armored slit, not a dome head).
    box(0.55, 0.28, 0.55, m.glow, 0.15, 0.5, 0),
    box(0.7, 0.12, 0.7, m.dark, 0.1, 0.62, 0),
    // Heavy twin thrusters.
    box(0.7, 0.38, 0.48, m.engine, -1.25, 0.14, 0.55),
    box(0.7, 0.38, 0.48, m.engine, -1.25, 0.14, -0.55),
    cyl(0.16, 0.22, 0.22, m.glow, -1.68, 0.14, 0.55, 0, 0, Math.PI / 2, 6),
    cyl(0.16, 0.22, 0.22, m.glow, -1.68, 0.14, -0.55, 0, 0, Math.PI / 2, 6),
    // Side skirt armor.
    box(1.3, 0.12, 0.22, m.dark, -0.15, 0.0, 0.95),
    box(1.3, 0.12, 0.22, m.dark, -0.15, 0.0, -0.95),
    // Dorsal ammo / plate ridge.
    box(1.1, 0.22, 0.85, m.hull, -0.35, 0.52, 0),
  );
};

/**
 * Vanguard — long heavy gunship.
 * Planform: armored center spine, four visible hardpoint pods, split engines.
 */
const buildVanguard: Builder = (root, m) => {
  root.add(
    box(2.65, 0.48, 0.78, m.hull, -0.05, 0.18, 0),
    box(0.65, 0.34, 0.58, m.glow, 0.72, 0.46, 0),
    box(0.55, 0.24, 1.55, m.plate, 0.35, 0.12, 0),
    // Four forward hardpoint housings make the gunship role legible from above.
    box(1.15, 0.24, 0.26, m.dark, 0.45, 0.15, 0.9),
    box(1.15, 0.24, 0.26, m.dark, 0.45, 0.15, -0.9),
    cyl(0.08, 0.1, 0.8, m.glow, 1.25, 0.15, 0.9, 0, 0, Math.PI / 2, 6),
    cyl(0.08, 0.1, 0.8, m.glow, 1.25, 0.15, -0.9, 0, 0, Math.PI / 2, 6),
    // Split armored engines and a broad ventral keel.
    box(0.95, 0.36, 0.48, m.engine, -1.25, 0.12, 0.58),
    box(0.95, 0.36, 0.48, m.engine, -1.25, 0.12, -0.58),
    cyl(0.15, 0.21, 0.24, m.glow, -1.8, 0.12, 0.58, 0, 0, Math.PI / 2, 6),
    cyl(0.15, 0.21, 0.24, m.glow, -1.8, 0.12, -0.58, 0, 0, Math.PI / 2, 6),
    box(1.65, 0.12, 0.3, m.dark, -0.2, 0, 0),
    box(0.85, 0.2, 0.55, m.plate, -0.55, 0.5, 0),
  );
};

/**
 * Reclaimer — field refinery tug.
 * Planform: central processing bay, paired collection claws, visible scrap bins.
 */
const buildReclaimer: Builder = (root, m) => {
  root.add(
    box(2.2, 0.48, 1.05, m.hull, -0.15, 0.16, 0),
    box(1.2, 0.52, 0.82, m.plate, -0.5, 0.55, 0),
    box(0.65, 0.34, 0.7, m.glow, 0.92, 0.42, 0),
    // Forward collection arms and inward-facing crusher teeth.
    box(1.2, 0.18, 0.24, m.dark, 0.85, 0.1, 0.9, 0, 0.18, 0),
    box(1.2, 0.18, 0.24, m.dark, 0.85, 0.1, -0.9, 0, -0.18, 0),
    box(0.35, 0.32, 0.2, m.plate, 1.45, 0.12, 0.72),
    box(0.35, 0.32, 0.2, m.plate, 1.45, 0.12, -0.72),
    // Side sorting bins make the economy role legible from above.
    box(0.95, 0.42, 0.52, m.engine, -0.2, 0.18, 1.02),
    box(0.95, 0.42, 0.52, m.engine, -0.2, 0.18, -1.02),
    box(0.7, 0.34, 0.45, m.engine, -1.35, 0.12, 0.55),
    box(0.7, 0.34, 0.45, m.engine, -1.35, 0.12, -0.55),
    cyl(0.14, 0.2, 0.24, m.glow, -1.78, 0.12, 0.55, 0, 0, Math.PI / 2, 6),
    cyl(0.14, 0.2, 0.24, m.glow, -1.78, 0.12, -0.55, 0, 0, Math.PI / 2, 6),
  );
};

/**
 * Corsair — scrap-fed raider.
 * Planform: narrow attack spine, split boarding prongs, exposed capacitor drums.
 */
const buildCorsair: Builder = (root, m) => {
  root.add(
    box(2.7, 0.34, 0.56, m.hull, 0.05, 0.14, 0),
    box(0.65, 0.28, 0.48, m.glow, 0.72, 0.39, 0),
    // Split forward prongs keep the raider readable from the chase camera.
    box(1.35, 0.16, 0.22, m.dark, 0.9, 0.08, 0.58, 0, -0.15, 0),
    box(1.35, 0.16, 0.22, m.dark, 0.9, 0.08, -0.58, 0, 0.15, 0),
    cyl(0.07, 0.1, 0.65, m.glow, 1.72, 0.1, 0.48, 0, 0, Math.PI / 2, 5),
    cyl(0.07, 0.1, 0.65, m.glow, 1.72, 0.1, -0.48, 0, 0, Math.PI / 2, 5),
    // Exposed raid-capacitor drums advertise the cargo-powered damage loop.
    cyl(0.3, 0.3, 0.9, m.glow, -0.05, 0.3, 0.78, Math.PI / 2, 0, 0, 8),
    cyl(0.3, 0.3, 0.9, m.glow, -0.05, 0.3, -0.78, Math.PI / 2, 0, 0, 8),
    box(0.9, 0.24, 0.32, m.plate, -0.25, 0.08, 0.92),
    box(0.9, 0.24, 0.32, m.plate, -0.25, 0.08, -0.92),
    box(1.0, 0.3, 0.38, m.engine, -1.15, 0.1, 0.5),
    box(1.0, 0.3, 0.38, m.engine, -1.15, 0.1, -0.5),
    cyl(0.13, 0.18, 0.24, m.glow, -1.75, 0.1, 0.5, 0, 0, Math.PI / 2, 6),
    cyl(0.13, 0.18, 0.24, m.glow, -1.75, 0.1, -0.5, 0, 0, Math.PI / 2, 6),
  );
};

/**
 * Fluxrunner — boost-fed shield interceptor.
 * Planform: narrow racing hull, forward field forks, paired converter coils.
 */
const buildFluxrunner: Builder = (root, m) => {
  root.add(
    box(2.85, 0.3, 0.5, m.hull, 0.05, 0.12, 0),
    box(0.68, 0.24, 0.38, m.glow, 0.7, 0.35, 0),
    // Split field forks carry shield charge ahead of the hull.
    box(1.25, 0.13, 0.18, m.plate, 1.0, 0.08, 0.6, 0, -0.12, 0),
    box(1.25, 0.13, 0.18, m.plate, 1.0, 0.08, -0.6, 0, 0.12, 0),
    cyl(0.09, 0.13, 0.5, m.glow, 1.68, 0.1, 0.52, 0, 0, Math.PI / 2, 8),
    cyl(0.09, 0.13, 0.5, m.glow, 1.68, 0.1, -0.52, 0, 0, Math.PI / 2, 8),
    // Paired transverse converter coils advertise the boost-to-shield loop.
    cyl(0.3, 0.3, 0.8, m.glow, -0.15, 0.26, 0.68, Math.PI / 2, 0, 0, 10),
    cyl(0.3, 0.3, 0.8, m.glow, -0.15, 0.26, -0.68, Math.PI / 2, 0, 0, 10),
    box(0.95, 0.2, 0.24, m.dark, -0.35, 0.06, 0.92, 0, 0.18, 0),
    box(0.95, 0.2, 0.24, m.dark, -0.35, 0.06, -0.92, 0, -0.18, 0),
    box(1.1, 0.3, 0.34, m.engine, -1.15, 0.1, 0.48),
    box(1.1, 0.3, 0.34, m.engine, -1.15, 0.1, -0.48),
    cyl(0.12, 0.18, 0.28, m.glow, -1.82, 0.1, 0.48, 0, 0, Math.PI / 2, 7),
    cyl(0.12, 0.18, 0.28, m.glow, -1.82, 0.1, -0.48, 0, 0, Math.PI / 2, 7),
  );
};

/** Jackal — lean pursuit craft with exposed kill-charge accumulator. */
const buildJackal: Builder = (root, m) => {
  root.add(
    box(2.65, 0.32, 0.52, m.hull, 0.1, 0.12, 0),
    box(0.55, 0.24, 0.38, m.glow, 0.65, 0.35, 0),
    box(1.4, 0.12, 0.2, m.dark, 0.55, 0.06, 0.7, 0, -0.22, 0),
    box(1.4, 0.12, 0.2, m.dark, 0.55, 0.06, -0.7, 0, 0.22, 0),
    cyl(0.08, 0.12, 0.65, m.glow, 1.55, 0.08, 0.58, 0, 0, Math.PI / 2, 6),
    cyl(0.08, 0.12, 0.65, m.glow, 1.55, 0.08, -0.58, 0, 0, Math.PI / 2, 6),
    // Central accumulator and oversized pursuit engines.
    cyl(0.28, 0.28, 0.75, m.glow, -0.25, 0.35, 0, 0, 0, Math.PI / 2, 9),
    box(1.15, 0.3, 0.34, m.engine, -1.05, 0.08, 0.48),
    box(1.15, 0.3, 0.34, m.engine, -1.05, 0.08, -0.48),
    cyl(0.13, 0.19, 0.3, m.glow, -1.73, 0.08, 0.48, 0, 0, Math.PI / 2, 7),
    cyl(0.13, 0.19, 0.3, m.glow, -1.73, 0.08, -0.48, 0, 0, Math.PI / 2, 7),
  );
};

/** Slipstream — four-engine boost cycler with exposed ignition capacitors. */
const buildSlipstream: Builder = (root, m) => {
  root.add(
    box(2.9, 0.28, 0.46, m.hull, 0.15, 0.1, 0),
    box(0.62, 0.22, 0.36, m.glow, 0.78, 0.33, 0),
    // Swept cycle vanes and paired capacitor rails.
    box(1.45, 0.12, 0.2, m.plate, 0.1, 0.05, 0.88, 0, -0.32, 0),
    box(1.45, 0.12, 0.2, m.plate, 0.1, 0.05, -0.88, 0, 0.32, 0),
    cyl(0.12, 0.12, 1.05, m.glow, 0.35, 0.28, 0.62, 0, 0, Math.PI / 2, 9),
    cyl(0.12, 0.12, 1.05, m.glow, 0.35, 0.28, -0.62, 0, 0, Math.PI / 2, 9),
    // Four short engines make boost ignition legible from above.
    box(0.85, 0.25, 0.28, m.engine, -1.22, 0.08, 0.72),
    box(0.85, 0.25, 0.28, m.engine, -1.22, 0.08, -0.72),
    box(0.72, 0.22, 0.24, m.engine, -1.36, 0.05, 0.28),
    box(0.72, 0.22, 0.24, m.engine, -1.36, 0.05, -0.28),
    cyl(0.1, 0.16, 0.24, m.glow, -1.78, 0.08, 0.72, 0, 0, Math.PI / 2, 7),
    cyl(0.1, 0.16, 0.24, m.glow, -1.78, 0.08, -0.72, 0, 0, Math.PI / 2, 7),
    cyl(0.08, 0.13, 0.2, m.glow, -1.75, 0.05, 0.28, 0, 0, Math.PI / 2, 7),
    cyl(0.08, 0.13, 0.2, m.glow, -1.75, 0.05, -0.28, 0, 0, Math.PI / 2, 7),
  );
};

/** Backlash — split-shield counterstrike frame with exposed discharge rails. */
const buildBacklash: Builder = (root, m) => {
  root.add(
    box(2.55, 0.34, 0.62, m.hull, 0.05, 0.12, 0),
    box(0.62, 0.25, 0.44, m.glow, 0.7, 0.38, 0),
    // Wide forward shield forks make the break-trigger identity legible.
    box(1.35, 0.17, 0.28, m.plate, 0.72, 0.08, 0.78, 0, -0.16, 0),
    box(1.35, 0.17, 0.28, m.plate, 0.72, 0.08, -0.78, 0, 0.16, 0),
    box(0.28, 0.3, 0.42, m.glow, 1.48, 0.12, 0.63),
    box(0.28, 0.3, 0.42, m.glow, 1.48, 0.12, -0.63),
    // Long side rails dump shield charge into the weapon bus.
    cyl(0.12, 0.12, 1.45, m.glow, -0.05, 0.3, 0.72, 0, 0, Math.PI / 2, 9),
    cyl(0.12, 0.12, 1.45, m.glow, -0.05, 0.3, -0.72, 0, 0, Math.PI / 2, 9),
    box(0.82, 0.2, 0.26, m.dark, -0.48, 0.06, 0.98, 0, 0.18, 0),
    box(0.82, 0.2, 0.26, m.dark, -0.48, 0.06, -0.98, 0, -0.18, 0),
    box(1.0, 0.3, 0.36, m.engine, -1.18, 0.1, 0.5),
    box(1.0, 0.3, 0.36, m.engine, -1.18, 0.1, -0.5),
    cyl(0.13, 0.19, 0.26, m.glow, -1.77, 0.1, 0.5, 0, 0, Math.PI / 2, 7),
    cyl(0.13, 0.19, 0.26, m.glow, -1.77, 0.1, -0.5, 0, 0, Math.PI / 2, 7),
  );
};

/** Magpie — collector skiff with a visible central ordnance autoloader. */
const buildMagpie: Builder = (root, m) => {
  root.add(
    box(2.5, 0.4, 0.78, m.hull, -0.05, 0.14, 0),
    box(0.64, 0.28, 0.5, m.glow, 0.7, 0.42, 0),
    // Forward salvage scoops feed the central autoloader drum.
    box(1.25, 0.14, 0.24, m.dark, 0.88, 0.07, 0.82, 0, -0.2, 0),
    box(1.25, 0.14, 0.24, m.dark, 0.88, 0.07, -0.82, 0, 0.2, 0),
    box(0.34, 0.3, 0.24, m.plate, 1.5, 0.1, 0.66),
    box(0.34, 0.3, 0.24, m.plate, 1.5, 0.1, -0.66),
    cyl(0.38, 0.38, 0.92, m.glow, -0.08, 0.48, 0, Math.PI / 2, 0, 0, 10),
    // Side cargo panniers keep its collection role readable from above.
    box(0.95, 0.38, 0.48, m.plate, -0.42, 0.15, 0.92),
    box(0.95, 0.38, 0.48, m.plate, -0.42, 0.15, -0.92),
    box(0.95, 0.3, 0.36, m.engine, -1.25, 0.1, 0.5),
    box(0.95, 0.3, 0.36, m.engine, -1.25, 0.1, -0.5),
    cyl(0.13, 0.19, 0.26, m.glow, -1.82, 0.1, 0.5, 0, 0, Math.PI / 2, 7),
    cyl(0.13, 0.19, 0.26, m.glow, -1.82, 0.1, -0.5, 0, 0, Math.PI / 2, 7),
  );
};

/** Breachwing — split prow striker with exposed shield-break cycle bus. */
const buildBreachwing: Builder = (root, m) => {
  root.add(
    box(2.7, 0.32, 0.58, m.hull, 0.0, 0.12, 0),
    box(0.58, 0.24, 0.4, m.glow, 0.55, 0.36, 0),
    // Twin forward drills make the shield-break role readable from above.
    cyl(0.07, 0.15, 1.15, m.plate, 1.45, 0.08, 0.58, 0, 0, Math.PI / 2, 7),
    cyl(0.07, 0.15, 1.15, m.plate, 1.45, 0.08, -0.58, 0, 0, Math.PI / 2, 7),
    box(1.25, 0.12, 0.22, m.dark, 0.42, 0.04, 0.86, 0, -0.25, 0),
    box(1.25, 0.12, 0.22, m.dark, 0.42, 0.04, -0.86, 0, 0.25, 0),
    // Transverse couplers feed a broken shield pulse into every hardpoint.
    cyl(0.18, 0.18, 1.35, m.glow, -0.1, 0.34, 0, Math.PI / 2, 0, 0, 9),
    box(0.92, 0.28, 0.34, m.engine, -1.2, 0.08, 0.48),
    box(0.92, 0.28, 0.34, m.engine, -1.2, 0.08, -0.48),
    cyl(0.12, 0.18, 0.25, m.glow, -1.75, 0.08, 0.48, 0, 0, Math.PI / 2, 7),
    cyl(0.12, 0.18, 0.25, m.glow, -1.75, 0.08, -0.48, 0, 0, Math.PI / 2, 7),
  );
};

/** Tetherwing — high-speed recall craft wrapped around a visible tether reel. */
const buildTetherwing: Builder = (root, m) => {
  root.add(
    box(2.9, 0.28, 0.48, m.hull, 0.08, 0.1, 0),
    box(0.58, 0.22, 0.34, m.glow, 0.72, 0.32, 0),
    // Wide swept guides keep the returning-disc identity legible from above.
    box(1.55, 0.1, 0.2, m.dark, 0.08, 0.04, 0.88, 0, -0.34, 0),
    box(1.55, 0.1, 0.2, m.dark, 0.08, 0.04, -0.88, 0, 0.34, 0),
    cyl(0.44, 0.44, 0.54, m.glow, -0.15, 0.38, 0, 0, 0, Math.PI / 2, 12),
    cyl(0.18, 0.18, 1.2, m.plate, 0.15, 0.18, 0.62, 0, 0, Math.PI / 2, 8),
    cyl(0.18, 0.18, 1.2, m.plate, 0.15, 0.18, -0.62, 0, 0, Math.PI / 2, 8),
    box(1.05, 0.26, 0.3, m.engine, -1.18, 0.06, 0.46),
    box(1.05, 0.26, 0.3, m.engine, -1.18, 0.06, -0.46),
    cyl(0.11, 0.17, 0.26, m.glow, -1.8, 0.06, 0.46, 0, 0, Math.PI / 2, 7),
    cyl(0.11, 0.17, 0.26, m.glow, -1.8, 0.06, -0.46, 0, 0, Math.PI / 2, 7),
  );
};

const FRAME_BUILDERS: Record<ShipFrameId, Builder> = {
  skimmer: buildSkimmer,
  hauler: buildHauler,
  interceptor: buildInterceptor,
  bulwark: buildBulwark,
  vanguard: buildVanguard,
  reclaimer: buildReclaimer,
  corsair: buildCorsair,
  jackal: buildJackal,
  fluxrunner: buildFluxrunner,
  slipstream: buildSlipstream,
  backlash: buildBacklash,
  magpie: buildMagpie,
  breachwing: buildBreachwing,
  tetherwing: buildTetherwing,
};

/** Soft radial contact-shadow texture for a ground blob plane. */
export function createBlobShadowTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return new THREE.CanvasTexture(canvas);
  }
  const g = ctx.createRadialGradient(size / 2, size / 2, size * 0.04, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(0,0,0,0.72)");
  g.addColorStop(0.22, "rgba(0,0,0,0.42)");
  g.addColorStop(0.55, "rgba(0,0,0,0.16)");
  g.addColorStop(0.82, "rgba(0,0,0,0.04)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

export function createBlobShadow(): THREE.Mesh {
  const tex = createBlobShadowTexture();
  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    opacity: 0.95,
    color: 0x050403,
  });
  // Sized for ~3 m planform at SHIP_SCALE; world-view further scales by altitude.
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(5.8, 5.8), mat);
  mesh.rotation.order = "YXZ";
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = -1;
  return mesh;
}

/** Aim marker: outer ring + nose chevron. Yaw with aim facing, not hull travel yaw. */
export function createAimReticle(): THREE.Group {
  const group = new THREE.Group();
  group.name = "aim_reticle";

  // Sized just outside the smaller hull footprint for twin-stick aim literacy.
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(3.15, 3.45, 40),
    new THREE.MeshBasicMaterial({
      color: 0xd4a574,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;

  const chevron = new THREE.Mesh(
    new THREE.ConeGeometry(0.45, 1.05, 3),
    new THREE.MeshBasicMaterial({
      color: 0xe0b07a,
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
    }),
  );
  // Point along local +X (aim nose); cone default points +Y.
  chevron.rotation.order = "YXZ";
  chevron.rotation.z = -Math.PI / 2;
  chevron.position.set(4.2, 0.08, 0);

  group.add(ring, chevron);
  return group;
}
